import { animate, style, transition, trigger } from '@angular/animations';
import { ActiveDescendantKeyManager, CdkTrapFocus } from '@angular/cdk/a11y';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  Inject,
  Input,
  Optional,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { StopPropagationDirective } from '@examdojo/core/stop-propagation';
import { TooltipDirective } from '@examdojo/core/tooltip';
import { SelectOption } from '@examdojo/core/util/select-option';
import { TextInputComponent } from '@examdojo/ui/input';
import { TranslocoPipe } from '@jsverse/transloco';
import { distinctUntilChanged, EMPTY, filter, merge, startWith, switchMap, tap } from 'rxjs';
import { SMART_LINK_CONFIG, SmartLinkConfig } from '../smart-link-config.model';
import { SmartLinkOptionDirective } from './smart-link-option.directive';

@Component({
  selector: 'dojo-smart-link-input',
  templateUrl: './smart-link-input.component.html',
  styleUrls: ['./smart-link-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TextInputComponent,
    IconComponent,
    IconButtonComponent,
    ReactiveFormsModule,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    CdkTrapFocus,
    StopPropagationDirective,
    SmartLinkOptionDirective,
    TooltipDirective,
    TranslocoPipe,
  ],
  animations: [
    trigger('dropdownAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('150ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
  ],
  host: { class: 'block' },
})
export class SmartLinkInputComponent {
  constructor(@Optional() @Inject(SMART_LINK_CONFIG) private readonly smartLinkConfig: SmartLinkConfig | null) {
    merge(this.loadOptionsEffect(), this.syncFilterValueEffect(), this.syncMatchedLabelEffect())
      .pipe(takeUntilDestroyed())
      .subscribe();

    effect(() => {
      this.keyManager = new ActiveDescendantKeyManager(this.optionDirectives()).withWrap();
    });
  }

  @Input({ required: true }) formCtrl!: FormControl<string>;
  @Input() label?: string;
  @Input() placeholder?: string;
  readonly hasResolver = !!this.smartLinkConfig?.resolve;
  readonly hasOptions = computed(() => this.options().length > 0);
  readonly dropdownOpen = signal(false);
  readonly filterCtrl = new FormControl<string>('', { nonNullable: true });
  readonly matchedLabel = signal<string | null>(null);

  private readonly optionDirectives = viewChildren(SmartLinkOptionDirective);
  private keyManager: ActiveDescendantKeyManager<SmartLinkOptionDirective> | undefined;

  private readonly options = signal<Array<SelectOption<string>>>([]);

  readonly filteredOptions = computed(() => {
    const query = this.filterValue().toLowerCase();
    if (!query) {
      return this.options();
    }
    return this.options().filter((opt) => opt.label.toLowerCase().includes(query));
  });

  private readonly filterValue = signal('');

  toggleDropdown(): void {
    this.dropdownOpen.update((open) => !open);
    if (this.dropdownOpen()) {
      this.filterCtrl.reset();
      this.filterValue.set('');
    }
  }

  closeDropdown(): void {
    this.dropdownOpen.set(false);
  }

  onFilterKeyDown(event: KeyboardEvent): void {
    const keyManager = this.keyManager;
    if (!keyManager) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const index = keyManager.activeItemIndex;
      if (index !== null && index >= 0) {
        this.selectOption(this.filteredOptions()[index]);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown();
    } else {
      keyManager.onKeydown(event);
    }
  }

  resolveLink(): void {
    const value = this.formCtrl.value;
    if (value && this.smartLinkConfig?.resolve) {
      this.smartLinkConfig.resolve(value, { url: window.location.href });
    }
  }

  selectOption(option: SelectOption<string>): void {
    this.formCtrl.setValue(option.value);
    this.formCtrl.markAsDirty();
    this.matchedLabel.set(option.label);
    this.closeDropdown();
  }

  private loadOptionsEffect() {
    if (!this.smartLinkConfig?.selectOptions) {
      return EMPTY;
    }

    return this.smartLinkConfig.selectOptions().pipe(tap((opts) => this.options.set(opts)));
  }

  private syncFilterValueEffect() {
    return this.filterCtrl.valueChanges.pipe(tap((v) => this.filterValue.set(v)));
  }

  private syncMatchedLabelEffect() {
    return toObservable(this.options).pipe(
      filter((opts) => opts.length > 0),
      switchMap(() =>
        this.formCtrl.valueChanges.pipe(
          startWith(this.formCtrl.value),
          distinctUntilChanged(),
          tap((value) => {
            const match = this.options().find((opt) => opt.value === value);
            this.matchedLabel.set(match?.label ?? null);
          }),
        ),
      ),
    );
  }
}
